import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listUsers, inviteUser, updateUser, deleteUser } from '@/api/auth';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Modal from '@/components/ui/Modal';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Table, { type Column } from '@/components/ui/Table';
import IconButton from '@/components/ui/IconButton';
import RolesMatrix from '@/components/admin/RolesMatrix';
import AccessAssignmentsModal from '@/components/admin/AccessAssignmentsModal';
import { Pencil, Ban, RotateCcw, Trash2, KeyRound } from 'lucide-react';
import type { User, UserRole } from '@/types';
import { roleLabel } from '@/utils/format';

const roleOptions: { value: UserRole; label: string }[] = [
  { value: 'USER', label: 'User' },
  { value: 'GROUP_FINANCE', label: 'Group Finance' },
  { value: 'ADMIN', label: 'Admin' },
];

function roleBadge(role: UserRole) {
  const variantMap: Record<UserRole, 'blue' | 'green' | 'amber' | 'grey' | 'red'> = {
    ADMIN: 'red',
    GROUP_FINANCE: 'green',
    USER: 'grey',
  };
  return <Badge variant={variantMap[role]}>{roleLabel(role)}</Badge>;
}

export default function UsersAdminPage() {
  const qc = useQueryClient();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<UserRole>('USER');
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');

  const [editRole, setEditRole] = useState<UserRole>('USER');
  const [editName, setEditName] = useState('');
  const [editError, setEditError] = useState('');

  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [accessUser, setAccessUser] = useState<User | null>(null);
  const [deleteError, setDeleteError] = useState('');

  const { data: users, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: listUsers,
  });

  const inviteMutation = useMutation({
    mutationFn: () => inviteUser({ email: inviteEmail.trim(), role: inviteRole }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      setInviteSuccess(`Invite sent to ${inviteEmail}`);
      setInviteEmail('');
    },
    onError: (err: unknown) => {
      setInviteError(
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? 'Invite failed',
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, name, role, isActive }: { id: string; name: string; role: UserRole; isActive: boolean }) =>
      updateUser(id, { name, role, isActive }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      setEditUser(null);
    },
    onError: (err: unknown) => {
      setEditError(
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? 'Update failed',
      );
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => updateUser(id, { isActive: false }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => updateUser(id, { isActive: true }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      setDeleteTarget(null);
    },
    onError: (err: unknown) => {
      setDeleteError(
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? 'Delete failed',
      );
    },
  });

  const columns: Column<User>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (u) => (
        <div>
          <p className="font-medium text-gray-900">{u.name ?? '—'}</p>
          <p className="text-xs text-gray-600">{u.email}</p>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      render: (u) => roleBadge(u.role),
    },
    {
      key: 'status',
      header: 'Status',
      render: (u) => (
        <Badge variant={u.isActive ? 'green' : 'grey'}>
          {u.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'created',
      header: 'Joined',
      render: (u) => (
        <span className="text-xs text-gray-500">
          {new Date(u.createdAt).toLocaleDateString('en-GB')}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (u) => (
        <div className="flex items-center gap-1">
          <IconButton
            icon={Pencil}
            label="Edit user"
            variant="primary"
            onClick={(e) => { e.stopPropagation(); setEditUser(u); setEditRole(u.role); setEditName(u.name ?? ''); setEditError(''); }}
          />
          <IconButton
            icon={KeyRound}
            label="Entity/division access"
            variant="primary"
            onClick={(e) => { e.stopPropagation(); setAccessUser(u); }}
          />
          {u.isActive ? (
            <IconButton
              icon={Ban}
              label="Deactivate user"
              variant="danger"
              onClick={(e) => { e.stopPropagation(); deactivateMutation.mutate(u.id); }}
            />
          ) : (
            <IconButton
              icon={RotateCcw}
              label="Activate user"
              variant="success"
              onClick={(e) => { e.stopPropagation(); activateMutation.mutate(u.id); }}
            />
          )}
          <IconButton
            icon={Trash2}
            label="Delete user"
            variant="danger"
            onClick={(e) => { e.stopPropagation(); setDeleteTarget(u); setDeleteError(''); }}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage user accounts. Admin and Group Finance are super users; everyone else is a User whose entity access comes only from their access assignments (key icon).
          </p>
        </div>
        <Button size="sm" onClick={() => { setInviteOpen(true); setInviteError(''); setInviteSuccess(''); }}>
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z" />
          </svg>
          Invite user
        </Button>
      </div>

      <Table
        columns={columns}
        data={users ?? []}
        loading={isLoading}
        rowKey={(u) => u.id}
        emptyMessage="No users found."
      />

      <RolesMatrix />

      {/* Invite modal */}
      <Modal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title="Invite user"
        footer={
          !inviteSuccess ? (
            <>
              <Button variant="secondary" onClick={() => setInviteOpen(false)}>Cancel</Button>
              <Button
                loading={inviteMutation.isPending}
                onClick={() => { setInviteError(''); inviteMutation.mutate(); }}
              >
                Send invite
              </Button>
            </>
          ) : (
            <Button onClick={() => setInviteOpen(false)}>Done</Button>
          )
        }
      >
        {inviteSuccess ? (
          <div className="rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700">
            {inviteSuccess}
          </div>
        ) : (
          <div className="space-y-4">
            {inviteError && (
              <div role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                {inviteError}
              </div>
            )}
            <Input
              label="Email address"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="jane.smith@ashcourt.co.uk"
              required
            />
            <Select
              label="Role"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as UserRole)}
              options={roleOptions}
            />
          </div>
        )}
      </Modal>

      {/* Delete confirmation modal */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete user"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button
              variant="danger"
              loading={deleteMutation.isPending}
              onClick={() => { if (deleteTarget) deleteMutation.mutate(deleteTarget.id); }}
            >
              Delete
            </Button>
          </>
        }
      >
        {deleteTarget && (
          <div className="space-y-4">
            {deleteError && (
              <div role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                {deleteError}
              </div>
            )}
            <p className="text-sm text-gray-700">
              Are you sure you want to permanently delete{' '}
              <strong>{deleteTarget.name ?? deleteTarget.email}</strong>?
            </p>
            <p className="text-xs text-gray-500">
              This only works for users with no associated data. If the delete fails,
              disable the account instead.
            </p>
          </div>
        )}
      </Modal>

      {/* Edit role modal */}
      <Modal
        open={!!editUser}
        onClose={() => setEditUser(null)}
        title="Edit user"
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditUser(null)}>Cancel</Button>
            <Button
              loading={updateMutation.isPending}
              onClick={() => {
                if (!editUser) return;
                if (!editName.trim()) { setEditError('Name is required'); return; }
                setEditError('');
                updateMutation.mutate({ id: editUser.id, name: editName.trim(), role: editRole, isActive: editUser.isActive });
              }}
            >
              Save
            </Button>
          </>
        }
      >
        {editUser && (
          <div className="space-y-4">
            {editError && (
              <div role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                {editError}
              </div>
            )}
            <div className="rounded-lg bg-gray-50 px-4 py-3">
              <p className="text-xs text-gray-500">{editUser.email}</p>
            </div>
            <Input
              label="Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Full name"
            />
            <Select
              label="Role"
              value={editRole}
              onChange={(e) => setEditRole(e.target.value as UserRole)}
              options={roleOptions}
            />
          </div>
        )}
      </Modal>

      {/* Entity/division access assignments */}
      {accessUser && (
        <AccessAssignmentsModal user={accessUser} onClose={() => setAccessUser(null)} />
      )}
    </div>
  );
}
